import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Chip, Skeleton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdConfirmDialog,
  IpdSectionCard,
  IpdTextField,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { getAssignable, getEdServices } from "../api/emergencyApi";
import {
  firePendingCharges,
  listVisitServices,
  recordVisitService,
  removeVisitService,
} from "../api/emergencyBillingApi";
import { formatINR } from "../utils/charges";
import EmergencyDoctorAutocomplete from "./EmergencyDoctorAutocomplete";

const SPECIALIST = "specialist";

const CHARGE_CHIP = {
  posted: { label: "Charged", color: "success" },
  pending: { label: "Charging…", color: "default" },
  failed: { label: "Not charged", color: "warning" },
  cancelled: { label: "Charge cancelled", color: "default" },
};

const blankEntry = () => ({ kind: "", serviceId: null, doctorId: "", performedById: "", notes: "" });

/**
 * Procedures performed, equipment started and specialists called in during
 * the visit. Each entry is charged as it is saved — procedures and equipment
 * at their Department Setup fee, a specialist at that doctor's own fee — so
 * nothing done at the bedside is left for someone to remember at billing.
 * Removing an entry cancels its charge, or reverses it if it already posted.
 */
export default function ProceduresCard({ visit, disabled }) {
  const visitId = visit.id;
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogue, setCatalogue] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [adding, setAdding] = useState(false);
  const [entry, setEntry] = useState(blankEntry);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRecords(await listVisitServices(visitId));
      setError("");
    } catch (e) {
      setError(e?.message || "Could not load the procedures and equipment");
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    load();
  }, [load]);

  // What can be recorded: the department's active items and its doctors.
  // Only needed once someone starts recording.
  useEffect(() => {
    if (!adding) return;
    getEdServices()
      .then((data) => setCatalogue((data?.items || []).filter((s) => s.active)))
      .catch((e) => setError(e?.message || "Could not load the procedure and equipment list"));
    getAssignable()
      .then((data) => setDoctors(data?.doctors || []))
      .catch(() => setDoctors([]));
  }, [adding]);

  const setField = (patch) => setEntry((current) => ({ ...current, ...patch }));
  const isSpecialist = entry.kind === SPECIALIST;
  const ready = isSpecialist ? Boolean(entry.doctorId) : Boolean(entry.serviceId);

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const payload = isSpecialist
        ? { kind: SPECIALIST, doctor_id: entry.doctorId }
        : {
            kind: entry.kind,
            medical_service_id: entry.serviceId,
            ...(entry.performedById ? { performed_by_id: entry.performedById } : {}),
          };
      if (entry.notes.trim()) payload.notes = entry.notes.trim();
      const { message } = await recordVisitService(visitId, payload);
      firePendingCharges(visitId);
      setAdding(false);
      setEntry(blankEntry());
      setNotice(message || "Recorded.");
      await load();
    } catch (e) {
      setError(e?.message || "Could not save the entry");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { message, warning } = await removeVisitService(visitId, removing.id, reason.trim());
      setRemoving(null);
      setReason("");
      setNotice(warning || message || "Entry removed.");
      await load();
    } catch (e) {
      setError(e?.message || "Could not remove the entry");
    } finally {
      setBusy(false);
    }
  };

  return (
    <IpdSectionCard
      title="Procedures & equipment"
      subtitle="What was done, started or called in — charged as it is recorded"
      actions={
        !disabled &&
        !adding && (
          <IpdButton
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setEntry(blankEntry());
              setAdding(true);
            }}
          >
            Record
          </IpdButton>
        )
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {adding && (
        <Box
          sx={{
            mb: 2,
            p: 1.75,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
            border: `1px solid ${IPD_COLORS.border}`,
            bgcolor: IPD_COLORS.cardBg,
            display: "grid",
            gap: 1.75,
          }}
        >
          <Box>
            <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>What was done</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {catalogue.map((s) => (
                <IpdButton
                  key={s.service_id}
                  size="small"
                  variant={entry.serviceId === s.service_id ? "contained" : "outlined"}
                  onClick={() => setField({ kind: s.kind, serviceId: s.service_id, doctorId: "" })}
                >
                  {s.name}
                  {s.fee != null ? ` — ${formatINR(s.fee)}` : ""}
                </IpdButton>
              ))}
              <IpdButton
                size="small"
                variant={isSpecialist ? "contained" : "outlined"}
                onClick={() => setField({ kind: SPECIALIST, serviceId: null, performedById: "" })}
              >
                Specialist consult
              </IpdButton>
            </Box>
            {catalogue.length === 0 && (
              <Typography sx={{ mt: 1, fontSize: 12.5, color: IPD_COLORS.textMuted }}>
                No procedures or equipment are listed yet — add them in Department Setup.
              </Typography>
            )}
          </Box>

          {isSpecialist && (
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Specialist</Typography>
              <EmergencyDoctorAutocomplete
                doctors={doctors}
                value={entry.doctorId}
                onChange={(doctorId) => setField({ doctorId })}
                placeholder="Search the specialist who saw the patient"
                ariaLabel="Specialist"
              />
            </Box>
          )}
          {entry.kind && !isSpecialist && (
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Performed by (optional)</Typography>
              <EmergencyDoctorAutocomplete
                doctors={doctors}
                value={entry.performedById}
                onChange={(performedById) => setField({ performedById })}
                placeholder="Search the doctor who performed it"
                ariaLabel="Performed by"
              />
            </Box>
          )}

          <Box>
            <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Note (optional)</Typography>
            <IpdTextField
              value={entry.notes}
              onChange={(e) => {
                const notes = e.target.value;
                setField({ notes });
              }}
              size="small"
              fullWidth
              sx={ipdElevatedInputSx}
              inputProps={{ "aria-label": "Note" }}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <IpdButton variant="outlined" onClick={() => setAdding(false)} disabled={busy}>
              Cancel
            </IpdButton>
            <IpdButton onClick={save} loading={busy} disabled={!ready}>
              Save
            </IpdButton>
          </Box>
        </Box>
      )}

      {loading ? (
        <Skeleton variant="rounded" height={64} />
      ) : records.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textMuted }}>
          Nothing recorded yet.
        </Typography>
      ) : (
        <Box sx={{ display: "grid", gap: 1 }}>
          {records.map((r) => {
            const chip = CHARGE_CHIP[r.charge_status];
            return (
              <Box
                key={r.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  p: 1.25,
                  borderRadius: `${IPD_SHAPE.radiusMd}px`,
                  border: `1px solid ${IPD_COLORS.border}`,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    {r.kind === SPECIALIST ? `Specialist · ${r.performed_by_name || "—"}` : r.service_name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    {[
                      r.kind !== SPECIALIST && r.performed_by_name ? `by ${r.performed_by_name}` : null,
                      formatDateTime(r.performed_at),
                      r.fee != null ? formatINR(r.fee) : null,
                      r.notes,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                  {r.charge_status === "failed" && r.charge_error && (
                    <Typography sx={{ fontSize: 12, color: IPD_COLORS.warning.text }}>{r.charge_error}</Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                  {chip && <Chip size="small" label={chip.label} color={chip.color} />}
                  {!disabled && (
                    <IpdButton
                      size="small"
                      variant="outlined"
                      tone="red"
                      onClick={() => {
                        setReason("");
                        setRemoving(r);
                      }}
                    >
                      Remove
                    </IpdButton>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <IpdConfirmDialog
        open={Boolean(removing)}
        destructive
        requireReason
        reasonLabel="Why is this being removed?"
        reasonValue={reason}
        onReasonChange={setReason}
        title="Remove this entry?"
        description={
          removing
            ? "The entry stays in the visit's history with your reason. Its charge is cancelled, or reversed if it already reached billing."
            : ""
        }
        confirmLabel="Remove entry"
        loading={busy}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </IpdSectionCard>
  );
}
